import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { IAttendanceDashboardData } from '../../../../apis/dashboard/dashboardAttendance.interfaces';
import { ICommunicationDashboardData } from '../../../../apis/dashboard/dashboardCommunications.interfaces';
import { IEventsDashboardData as ILegacyEventsDashboardData } from '../../../../apis/dashboard/dashboardEvents.interfaces';
import { IFinanceDashboardData as ILegacyFinanceDashboardData } from '../../../../apis/dashboard/dashboardFinance.interfaces';
import { IHealthDashboardData } from '../../../../apis/dashboard/dashboardHealth.interfaces';
import { IInventoryDashboardData } from '../../../../apis/dashboard/dashboardInventory.interfaces';
import { IStaffDashboardData } from '../../../../apis/dashboard/dashboardStaff.interfaces';
import { IStudentsDashboardData } from '../../../../apis/dashboard/dashboardStudents.interfaces';
// New interfaces
import {
  IFinanceDashboardData,
  IPeopleDashboardData,
  IEventsDashboardData,
} from '../../../../apis/dashboard/dashboard.interfaces';
import { useDashboardAttendanceHook } from '../hooks/useDashboardAttendanceHook';
import { useDashboardCommunicationsHook } from '../hooks/useDashboardCommunicationsHook';
import { useDashboardEventsHook } from '../hooks/useDashboardEventsHook';
import { useDashboardFinanceHook } from '../hooks/useDashboardFinanceHook';
import { useDashboardHealthHook } from '../hooks/useDashboardHealthHook';
import { useDashboardInventoryHook } from '../hooks/useDashboardInventoryHook';
import { useDashboardStaffHook } from '../hooks/useDashboardStaffHook';
import { useDashboardStudentsHook } from '../hooks/useDashboardStudentsHook';
import { useDashboardPeopleHook } from '../hooks/useDashboardPeopleHook';

type TDashboardState = {
  errorMessage?: string;
  isLoading: boolean;
  // Legacy data (still used by some components)
  studentsDashboardData: IStudentsDashboardData | null;
  staffDashboardData: IStaffDashboardData | null;
  inventoryDashboardData: IInventoryDashboardData | null;
  healthDashboardData: IHealthDashboardData | null;
  financeDashboardData: ILegacyFinanceDashboardData | null;
  communicationsDashboardData: ICommunicationDashboardData | null;
  attendanceDashboardData: IAttendanceDashboardData | null;
  eventsDashboardData: ILegacyEventsDashboardData | null;
  // New data structures
  financeDashboardDataNew: IFinanceDashboardData | null;
  peopleDashboardData: IPeopleDashboardData | null;
  eventsDashboardDataNew: IEventsDashboardData | null;
};

enum ACTION_TYPE {
  SET_STUDENTS_DATA = 'SET_STUDENTS_DATA',
  SET_STAFF_DATA = 'SET_STAFF_DATA',
  SET_INVENTORY_DATA = 'SET_INVENTORY_DATA',
  SET_HEALTH_DATA = 'SET_HEALTH_DATA',
  SET_FINANCE_DATA = 'SET_FINANCE_DATA',
  SET_COMMUNICATIONS_DATA = 'SET_COMMUNICATIONS_DATA',
  SET_ATTENDANCE_DATA = 'SET_ATTENDANCE_DATA',
  SET_EVENTS_DATA = 'SET_EVENTS_DATA',
  // New action types
  SET_FINANCE_DATA_NEW = 'SET_FINANCE_DATA_NEW',
  SET_PEOPLE_DATA = 'SET_PEOPLE_DATA',
  SET_EVENTS_DATA_NEW = 'SET_EVENTS_DATA_NEW',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

type Action =
  | {
      type: ACTION_TYPE.SET_STUDENTS_DATA;
      payload: IStudentsDashboardData | null;
    }
  | { type: ACTION_TYPE.SET_STAFF_DATA; payload: IStaffDashboardData | null }
  | {
      type: ACTION_TYPE.SET_INVENTORY_DATA;
      payload: IInventoryDashboardData | null;
    }
  | { type: ACTION_TYPE.SET_HEALTH_DATA; payload: IHealthDashboardData | null }
  | {
      type: ACTION_TYPE.SET_FINANCE_DATA;
      payload: ILegacyFinanceDashboardData | null;
    }
  | {
      type: ACTION_TYPE.SET_COMMUNICATIONS_DATA;
      payload: ICommunicationDashboardData | null;
    }
  | {
      type: ACTION_TYPE.SET_ATTENDANCE_DATA;
      payload: IAttendanceDashboardData | null;
    }
  | { type: ACTION_TYPE.SET_EVENTS_DATA; payload: ILegacyEventsDashboardData | null }
  | {
      type: ACTION_TYPE.SET_FINANCE_DATA_NEW;
      payload: IFinanceDashboardData | null;
    }
  | {
      type: ACTION_TYPE.SET_PEOPLE_DATA;
      payload: IPeopleDashboardData | null;
    }
  | {
      type: ACTION_TYPE.SET_EVENTS_DATA_NEW;
      payload: IEventsDashboardData | null;
    }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialState: TDashboardState = {
  errorMessage: undefined,
  isLoading: false,
  // Legacy data
  attendanceDashboardData: null,
  communicationsDashboardData: null,
  eventsDashboardData: null,
  financeDashboardData: null,
  healthDashboardData: null,
  inventoryDashboardData: null,
  staffDashboardData: null,
  studentsDashboardData: null,
  // New data structures
  financeDashboardDataNew: null,
  peopleDashboardData: null,
  eventsDashboardDataNew: null,
};

const DashboardContext = createContext<TDashboardState | undefined>(undefined);
const DashboardDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

const dashboardReducer = (
  state: TDashboardState,
  action: Action
): TDashboardState => {
  switch (action.type) {
    case ACTION_TYPE.SET_STUDENTS_DATA:
      return {
        ...state,
        studentsDashboardData: action.payload,
      };
    case ACTION_TYPE.SET_STAFF_DATA:
      return {
        ...state,
        staffDashboardData: action.payload,
      };
    case ACTION_TYPE.SET_INVENTORY_DATA:
      return {
        ...state,
        inventoryDashboardData: action.payload,
      };
    case ACTION_TYPE.SET_HEALTH_DATA:
      return {
        ...state,
        healthDashboardData: action.payload,
      };
    case ACTION_TYPE.SET_FINANCE_DATA:
      return {
        ...state,
        financeDashboardData: action.payload,
      };
    case ACTION_TYPE.SET_COMMUNICATIONS_DATA:
      return {
        ...state,
        communicationsDashboardData: action.payload,
      };
    case ACTION_TYPE.SET_ATTENDANCE_DATA:
      return {
        ...state,
        attendanceDashboardData: action.payload,
      };
    case ACTION_TYPE.SET_EVENTS_DATA:
      return {
        ...state,
        eventsDashboardData: action.payload,
      };
    case ACTION_TYPE.SET_FINANCE_DATA_NEW:
      return {
        ...state,
        financeDashboardDataNew: action.payload,
      };
    case ACTION_TYPE.SET_PEOPLE_DATA:
      return {
        ...state,
        peopleDashboardData: action.payload,
      };
    case ACTION_TYPE.SET_EVENTS_DATA_NEW:
      return {
        ...state,
        eventsDashboardDataNew: action.payload,
      };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const DashboardProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(dashboardReducer, initialState);

  const { data: studentsData, isLoading: isLoadingStudents } =
    useDashboardStudentsHook();
  const { data: staffData, isLoading: isLoadingStaff } =
    useDashboardStaffHook();
  const { data: inventoryData, isLoading: isLoadingInventory } =
    useDashboardInventoryHook();
  const { data: healthData, isLoading: isLoadingHealth } =
    useDashboardHealthHook();
  // Legacy hooks (still used by some components)
  const { data: communicationsData, isLoading: isLoadingCommunications } =
    useDashboardCommunicationsHook();
  const { data: attendanceData, isLoading: isLoadingAttendance } =
    useDashboardAttendanceHook();
  // New hooks (updated to use new APIs)
  const { data: financeDataNew, isLoading: isLoadingFinanceNew } =
    useDashboardFinanceHook();
  const { data: peopleData, isLoading: isLoadingPeople } =
    useDashboardPeopleHook();
  const { data: eventsDataNew, isLoading: isLoadingEventsNew } =
    useDashboardEventsHook();
  // Legacy data for backward compatibility (will be deprecated)
  // Note: financeData and eventsData now come from new hooks but stored in legacy fields for compatibility
  const financeData = financeDataNew as unknown as ILegacyFinanceDashboardData | null;
  const eventsData = eventsDataNew as unknown as ILegacyEventsDashboardData | null;
  const isLoadingFinance = isLoadingFinanceNew;
  const isLoadingEvents = isLoadingEventsNew;

  const isLoading = useMemo(
    () =>
      isLoadingStudents ||
      isLoadingStaff ||
      isLoadingInventory ||
      isLoadingHealth ||
      isLoadingFinance ||
      isLoadingCommunications ||
      isLoadingAttendance ||
      isLoadingEvents ||
      isLoadingFinanceNew ||
      isLoadingPeople ||
      isLoadingEventsNew,
    [
      isLoadingStudents,
      isLoadingStaff,
      isLoadingInventory,
      isLoadingHealth,
      isLoadingFinance,
      isLoadingCommunications,
      isLoadingAttendance,
      isLoadingEvents,
      isLoadingFinanceNew,
      isLoadingPeople,
      isLoadingEventsNew,
    ]
  );

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: isLoading });
  }, [isLoading]);

  useEffect(() => {
    if (studentsData) {
      dispatch({
        type: ACTION_TYPE.SET_STUDENTS_DATA,
        payload: studentsData,
      });
    }
  }, [studentsData]);

  useEffect(() => {
    if (staffData) {
      dispatch({
        type: ACTION_TYPE.SET_STAFF_DATA,
        payload: staffData,
      });
    }
  }, [staffData]);

  useEffect(() => {
    if (inventoryData) {
      dispatch({
        type: ACTION_TYPE.SET_INVENTORY_DATA,
        payload: inventoryData,
      });
    }
  }, [inventoryData]);

  useEffect(() => {
    if (healthData) {
      dispatch({
        type: ACTION_TYPE.SET_HEALTH_DATA,
        payload: healthData,
      });
    }
  }, [healthData]);

  useEffect(() => {
    if (financeData) {
      dispatch({
        type: ACTION_TYPE.SET_FINANCE_DATA,
        payload: financeData,
      });
    }
  }, [financeData]);

  useEffect(() => {
    if (communicationsData) {
      dispatch({
        type: ACTION_TYPE.SET_COMMUNICATIONS_DATA,
        payload: communicationsData,
      });
    }
  }, [communicationsData]);

  useEffect(() => {
    if (attendanceData) {
      dispatch({
        type: ACTION_TYPE.SET_ATTENDANCE_DATA,
        payload: attendanceData,
      });
    }
  }, [attendanceData]);

  useEffect(() => {
    if (eventsData) {
      dispatch({
        type: ACTION_TYPE.SET_EVENTS_DATA,
        payload: eventsData,
      });
    }
  }, [eventsData]);

  // New data effects
  useEffect(() => {
    if (financeDataNew) {
      dispatch({
        type: ACTION_TYPE.SET_FINANCE_DATA_NEW,
        payload: financeDataNew,
      });
    }
  }, [financeDataNew]);

  useEffect(() => {
    if (peopleData) {
      dispatch({
        type: ACTION_TYPE.SET_PEOPLE_DATA,
        payload: peopleData,
      });
    }
  }, [peopleData]);

  useEffect(() => {
    if (eventsDataNew) {
      dispatch({
        type: ACTION_TYPE.SET_EVENTS_DATA_NEW,
        payload: eventsDataNew,
      });
    }
  }, [eventsDataNew]);

  const value = useMemo(
    () => ({
      ...state,
      isLoading,
    }),
    [state, isLoading]
  );

  return (
    <DashboardContext.Provider value={value}>
      <DashboardDispatchContext.Provider value={dispatch}>
        {children}
      </DashboardDispatchContext.Provider>
    </DashboardContext.Provider>
  );
};

export const useDashboardState = () => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error(
      'useDashboardState must be used within a DashboardProvider'
    );
  }
  return context;
};

export const useDashboardActions = () => {
  const dispatch = useContext(DashboardDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useDashboardActions must be used within a DashboardProvider'
    );
  }

  return { dispatch };
};
